/**
 * Encrypted envelope of the deferred send — the E2E heart of the optional account.
 *
 * The sender (who may be a GUEST without an account) encrypts the file for ONE
 * trusted device of the recipient and leaves the envelope in the relay's
 * mailbox; the relay stores it opaque and can never open it:
 *
 *   ephemeral ECDH (P-256) × public key of the destination device
 *     → HKDF-SHA256 (random salt, domain info)
 *     → AES-256-GCM (random iv) over the framed payload.
 *
 * Properties: confidentiality and integrity (GCM authenticates; any
 * tampering with `ct`/`iv`/`epk`/`salt` breaks decryption), forward-ish
 * (new ephemeral key per envelope) and sender anonymity before the relay (the
 * sender only goes INSIDE the ciphertext, if the sender decides to include it).
 *
 * The plaintext payload is framed as: 4 LE bytes = length of the JSON
 * header + header (name/mime/size/…) + file bytes. Thus the
 * metadata also travels encrypted (the relay does not even see the NAME).
 */

import { isRecord } from "../p2p/guards";
import { fromBase64Url, toArrayBuffer, toBase64Url, utf8Decode, utf8Encode } from "./encoding";

const ENVELOPE_ALG = "ECDH-P256+HKDF-SHA256+A256GCM";
const HKDF_INFO = "enviar-mailbox-envelope-v1";
const ECDH_PARAMS: EcKeyGenParams = { name: "ECDH", namedCurve: "P-256" };

/** Metadata of the deferred file — it travels ENCRYPTED inside the envelope. */
export interface DeferredHeader {
  name: string;
  mime: string;
  size: number;
  sentAtMs: number;
  /** Self-declared sender (optional); the relay never sees it. */
  from?: string;
}

const MAX_HEADER_BYTES = 16 * 1024;

function subtle(): SubtleCrypto {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj?.subtle) {
    throw new Error("WebCrypto SubtleCrypto is not available in this environment");
  }
  return cryptoObj.subtle;
}

/** Frames header + file bytes into a single encryptable payload. */
export function encodeDeferredPayload(header: DeferredHeader, data: Uint8Array): Uint8Array {
  const headerBytes = utf8Encode(JSON.stringify(header));
  if (headerBytes.length > MAX_HEADER_BYTES) {
    throw new Error("deferred header too large");
  }
  const out = new Uint8Array(4 + headerBytes.length + data.length);
  new DataView(out.buffer).setUint32(0, headerBytes.length, true);
  out.set(headerBytes, 4);
  out.set(data, 4 + headerBytes.length);
  return out;
}

/** Unframes the decrypted payload. Throws `Error` if the structure does not fit. */
export function decodeDeferredPayload(payload: Uint8Array): {
  header: DeferredHeader;
  data: Uint8Array;
} {
  if (payload.length < 4) {
    throw new Error("deferred payload truncated");
  }
  const headerLen = new DataView(payload.buffer, payload.byteOffset, payload.byteLength).getUint32(
    0,
    true,
  );
  if (headerLen > MAX_HEADER_BYTES || 4 + headerLen > payload.length) {
    throw new Error("deferred payload corrupt");
  }
  const parsed: unknown = JSON.parse(utf8Decode(payload.subarray(4, 4 + headerLen)));
  if (
    !isRecord(parsed) ||
    typeof parsed.name !== "string" ||
    typeof parsed.mime !== "string" ||
    typeof parsed.size !== "number" ||
    typeof parsed.sentAtMs !== "number" ||
    (parsed.from !== undefined && typeof parsed.from !== "string")
  ) {
    throw new Error("deferred header corrupt");
  }
  const data = payload.subarray(4 + headerLen);
  if (data.length !== parsed.size) {
    throw new Error("deferred payload size mismatch");
  }
  const header: DeferredHeader = {
    name: parsed.name,
    mime: parsed.mime,
    size: parsed.size,
    sentAtMs: parsed.sentAtMs,
  };
  if (typeof parsed.from === "string") {
    header.from = parsed.from;
  }
  return { header, data };
}

async function deriveAesKey(
  sharedBits: ArrayBuffer,
  salt: Uint8Array,
  usage: "encrypt" | "decrypt",
): Promise<CryptoKey> {
  const s = subtle();
  const hkdfKey = await s.importKey("raw", sharedBits, "HKDF", false, ["deriveKey"]);
  return s.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: toArrayBuffer(salt),
      info: toArrayBuffer(utf8Encode(HKDF_INFO)),
    },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    [usage],
  );
}

/**
 * Encrypts a payload for the encryption public key (`encPub`, base64url) of
 * a trusted device. Returns the envelope as a JSON STRING — it is what
 * is deposited as-is in the mailbox (opaque for the server).
 */
export async function sealEnvelope(recipientEncPub: string, payload: Uint8Array): Promise<string> {
  const s = subtle();
  const recipientKey = await s.importKey(
    "raw",
    toArrayBuffer(fromBase64Url(recipientEncPub)),
    ECDH_PARAMS,
    false,
    [],
  );
  const ephemeral = await s.generateKey(ECDH_PARAMS, false, ["deriveBits"]);
  const sharedBits = await s.deriveBits(
    { name: "ECDH", public: recipientKey },
    ephemeral.privateKey,
    256,
  );
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const aesKey = await deriveAesKey(sharedBits, salt, "encrypt");
  const ciphertext = await s.encrypt({ name: "AES-GCM", iv }, aesKey, toArrayBuffer(payload));
  const epk = new Uint8Array(await s.exportKey("raw", ephemeral.publicKey));
  return JSON.stringify({
    v: 1,
    alg: ENVELOPE_ALG,
    epk: toBase64Url(epk),
    salt: toBase64Url(salt),
    iv: toBase64Url(iv),
    ct: toBase64Url(new Uint8Array(ciphertext)),
  });
}

/**
 * Opens an envelope with the device's ENCRYPTION PRIVATE key. Throws `Error`
 * if the envelope is corrupt, tampered with or addressed to another key (GCM does
 * not authenticate → decryption impossible).
 */
export async function openEnvelope(
  encPrivateKey: CryptoKey,
  envelopeJson: string,
): Promise<Uint8Array> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(envelopeJson);
  } catch {
    throw new Error("envelope is not valid JSON");
  }
  if (
    !isRecord(parsed) ||
    parsed.v !== 1 ||
    parsed.alg !== ENVELOPE_ALG ||
    typeof parsed.epk !== "string" ||
    typeof parsed.salt !== "string" ||
    typeof parsed.iv !== "string" ||
    typeof parsed.ct !== "string"
  ) {
    throw new Error("envelope structure is invalid");
  }
  const s = subtle();
  const ephemeralPub = await s.importKey(
    "raw",
    toArrayBuffer(fromBase64Url(parsed.epk)),
    ECDH_PARAMS,
    false,
    [],
  );
  const sharedBits = await s.deriveBits({ name: "ECDH", public: ephemeralPub }, encPrivateKey, 256);
  const aesKey = await deriveAesKey(sharedBits, fromBase64Url(parsed.salt), "decrypt");
  try {
    const plain = await s.decrypt(
      { name: "AES-GCM", iv: toArrayBuffer(fromBase64Url(parsed.iv)) },
      aesKey,
      toArrayBuffer(fromBase64Url(parsed.ct)),
    );
    return new Uint8Array(plain);
  } catch {
    // GCM: tampering or wrong key → same error (not distinguishable).
    throw new Error("envelope authentication failed");
  }
}
